import React, { useCallback, useMemo } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Model } from '../../../util/backendapi/models/api.interfaces';
import { Trans } from '@lingui/macro';
import ModalContent from 'components/base/modal/modalcontent';
import Loading from 'components/base/loading/loading';
import { useGetApi } from 'hooks/use-get-api';
import { errorToString } from 'util/backendapi/error';
import { AlertDanger, AlertInfo } from 'components/base/alert/alert';
import { SimpleSelectField } from 'components/base/form/simpleselect/simpleselectfield';
import ActionBlock from 'components/base/actionblock/actionblock';
import { ButtonPrimary } from 'components/base/button/button';
import ButtonHideModal from 'components/base/modal/buttonhidemodal';

interface Props {
  routeMarch: Model.RouteMarch;
  routeMarchObservationPoints: Model.ReportsRouteMarchObservationPoint[];
  createRouteMarchObsPoint: (
    routeMarchId: number,
    observationPointId: number,
    position: number
  ) => Promise<void>;
  hideModal: () => void;
}

export function AddRouteMarchObsPointPanel(props: Props) {
  const { routeMarch } = props;

  const [observationPointDetail] = useGetApi('/observation-points/', {
    site__area__in: routeMarch.areas,
    fields: ['id', 'code'],
  });

  const errorMessage = observationPointDetail.isError
    ? errorToString(observationPointDetail.error)
    : '';

  return (
    <AddRouteMarchObsPointPanelView
      {...props}
      observationPoints={observationPointDetail.data}
      isLoading={observationPointDetail.isLoading}
      errorMessage={errorMessage}
    />
  );
}

type ViewProps = Props & {
  observationPoints: Pick<Model.ObservationPoint, 'id' | 'code'>[] | null;
  isLoading: boolean;
  errorMessage: string;
};

interface FormikValues {
  observationPoint: number;
}

export function AddRouteMarchObsPointPanelView(props: ViewProps) {
  const {
    routeMarch,
    routeMarchObservationPoints,
    observationPoints,
    createRouteMarchObsPoint,
    hideModal,
    isLoading,
    errorMessage,
  } = props;

  const onSubmit = useCallback(
    async (values: FormikValues, actions: FormikHelpers<FormikValues>) => {
      actions.setSubmitting(true);
      const position =
        routeMarchObservationPoints.length > 0
          ? routeMarchObservationPoints[routeMarchObservationPoints.length - 1]
              .position + 1
          : 1;
      await createRouteMarchObsPoint(
        routeMarch.id,
        values.observationPoint,
        position
      );

      actions.setSubmitting(false);

      hideModal();
    },
    [
      routeMarch,
      routeMarchObservationPoints,
      createRouteMarchObsPoint,
      hideModal,
    ]
  );

  const validate = useCallback((values: FormikValues) => {
    if (values.observationPoint === 0) {
      return {
        observationPoint: <Trans>Please select an observation point</Trans>,
      };
    }
  }, []);

  const observationPointOptions = useMemo(() => {
    if (!observationPoints) return [];

    const filteredObservationPoints: Pick<
      Model.ObservationPointDecorated,
      'id' | 'code'
    >[] = observationPoints.filter((obsPoint) => {
      // Remove observation points that are already in route march
      const alreadyAdded = routeMarchObservationPoints.find(
        (routeMarchObsPoint) =>
          routeMarchObsPoint.observation_point === obsPoint.id
      );
      return !alreadyAdded;
    });

    return filteredObservationPoints.map((obsPoint) => {
      return {
        value: obsPoint.id,
        label: obsPoint.code,
      };
    });
  }, [observationPoints, routeMarchObservationPoints]);

  let modalContent;

  if (isLoading) {
    modalContent = <Loading />;
  } else if (errorMessage) {
    modalContent = <AlertDanger>{errorMessage}</AlertDanger>;
  } else if (observationPoints && observationPoints.length > 0) {
    modalContent = (
      <Formik
        initialValues={{ observationPoint: 0 }}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formik: FormikProps<FormikValues>) => (
          <Form>
            <legend>Observation Point</legend>
            <SimpleSelectField
              name="observationPoint"
              id="routemarchobservationpoint-obspoints"
              options={observationPointOptions}
              placeholder={<Trans>Select an observation point</Trans>}
              autoFocus={true}
            />
            <ActionBlock>
              <ButtonHideModal />
              <ButtonPrimary
                id="routemarchobservationpoint-add"
                type="submit"
                disabled={formik.isSubmitting}
                iconType="icon-plus"
              >
                Add
              </ButtonPrimary>
            </ActionBlock>
          </Form>
        )}
      </Formik>
    );
  } else {
    modalContent = (
      <AlertInfo>
        <Trans>No observation points available.</Trans>
      </AlertInfo>
    );
  }

  return (
    <ModalContent header={<Trans>Add an observation point</Trans>}>
      {modalContent}
    </ModalContent>
  );
}
